<template>
    <div class="sam-heatmap" ref="root">
        <div class="hm-scroll">
        <div class="hm-months" :style="{gridTemplateColumns: monthsColumns}">
            <span v-for="m in monthLabels" :key="m.col" :style="{gridColumnStart: m.col}">{{ m.label }}</span>
        </div>
        <div class="hm-body">
            <div class="hm-dow" :style="{gridTemplateRows: `repeat(7, ${cellPx}px)`, gap: gapPx + 'px'}">
                <span></span><span>Mon</span><span></span><span>Wed</span><span></span><span>Fri</span><span></span>
            </div>
            <div class="hm-cells" :style="{gridTemplateColumns: cellsColumns, gridTemplateRows: `repeat(7, ${cellPx}px)`, gap: gapPx + 'px'}">
                <div v-for="cell in cells"
                     :key="cell.key"
                     class="cell"
                     :class="cellClass(cell)"
                     :style="{gridColumn: cell.col, gridRow: cell.row, visibility: cell.inRange ? 'visible' : 'hidden'}"
                     :tabindex="cell.inRange ? 0 : -1"
                     :aria-label="cell.aria"
                     role="gridcell"
                     @mouseenter="showTip(cell, $event)"
                     @mouseleave="tip.visible = false"
                     @focus="showTip(cell, $event)"
                     @blur="tip.visible = false"
                     @click="select(cell)"
                     @keydown.enter.prevent="select(cell)"
                     @keydown.space.prevent="select(cell)">
                </div>
            </div>
        </div>
        <div class="hm-weeks" :style="{gridTemplateColumns: cellsColumns, gap: gapPx + 'px'}">
            <div v-for="w in weekStrip" :key="w.col" class="wk" :class="w.cls"
                 :style="{gridColumn: w.col}" :title="w.title || false"></div>
        </div>
        <div class="hm-weeks-label">Weekly load</div>
        </div>
        <div class="hm-tooltip" v-show="tip.visible" :style="{left: tip.x + 'px', top: tip.y + 'px'}" v-html="tip.html"></div>
    </div>
</template>

<script>
// Portable cross-course activity heatmap. Deliberately dependency-free
// (no Buefy/PrimeVue/store) so it can be lifted into v2 as-is: everything
// comes in as props, colors are CSS custom properties overridable by the
// host, and cell size solves from container width via ResizeObserver.
import moment from 'moment';

export default {
    name: 'ActivityHeatmap',
    props: {
        // [{date:'YYYY-MM-DD', mins, level:0-7, flag, overlap, contracted_mins, courses:[...]}]
        days: {type: Array, required: true},
        // [{week_start:'YYYY-MM-DD', mins, flag}]
        weeks: {type: Array, default: () => []},
        rangeFrom: {type: String, required: true},
        rangeTo: {type: String, required: true},
        selectedDate: {type: String, default: null},
    },
    data() {
        return {
            cellPx: 14,
            tip: {visible: false, html: '', x: 0, y: 0},
            resizeObserver: null,
        };
    },
    computed: {
        daysMap() {
            const map = {};
            for (const d of this.days) map[d.date] = d;
            return map;
        },
        weeksMap() {
            const map = {};
            for (const w of this.weeks) map[w.week_start] = w;
            return map;
        },
        gridStart() {
            return moment(this.rangeFrom).startOf('week');
        },
        gridEnd() {
            return moment(this.rangeTo).endOf('week');
        },
        totalWeeks() {
            return Math.round(this.gridEnd.diff(this.gridStart, 'days') / 7) + 1;
        },
        gapPx() {
            return this.cellPx >= 14 ? 3 : 2;
        },
        cellsColumns() {
            return `repeat(${this.totalWeeks}, ${this.cellPx}px)`;
        },
        monthsColumns() {
            return `repeat(${this.totalWeeks}, ${this.cellPx + this.gapPx}px)`;
        },
        cells() {
            const out = [];
            const from = this.rangeFrom, to = this.rangeTo;
            const cur = this.gridStart.clone();
            for (let i = 0; i < this.totalWeeks * 7; i++) {
                const date = cur.format('YYYY-MM-DD');
                const inRange = date >= from && date <= to;
                const data = inRange ? (this.daysMap[date] || null) : null;
                out.push({
                    key: date,
                    date,
                    col: Math.floor(i / 7) + 1,
                    row: (i % 7) + 1,
                    inRange,
                    data,
                    aria: inRange ? this.ariaFor(date, data) : '',
                });
                cur.add(1, 'day');
            }
            return out;
        },
        monthLabels() {
            const labels = [];
            let lastMonth = null;
            for (const cell of this.cells) {
                if (!cell.inRange || cell.row > 2) continue;
                const m = cell.date.slice(0, 7);
                if (m !== lastMonth) {
                    lastMonth = m;
                    labels.push({col: cell.col, label: moment(cell.date).format('MMM')});
                }
            }
            return labels;
        },
        weekStrip() {
            const out = [];
            const cur = this.gridStart.clone();
            for (let col = 1; col <= this.totalWeeks; col++) {
                const w = this.weeksMap[cur.format('YYYY-MM-DD')];
                const mins = w ? w.mins : 0;
                // blue magnitude ramp below the flag bands so normal weeks
                // still register; flags switch hue family like day cells
                let cls = 'w-base';
                if (w && w.flag) cls = 'f-' + w.flag;
                else if (mins >= 1800) cls = 'w-l4';
                else if (mins >= 1200) cls = 'w-l3';
                else if (mins >= 600) cls = 'w-l2';
                else if (mins > 0) cls = 'w-l1';
                out.push({
                    col,
                    cls,
                    title: mins > 0 ? this.fmtHours(mins) + ' logged this week' : '',
                });
                cur.add(7, 'days');
            }
            return out;
        },
    },
    mounted() {
        this.resizeObserver = new ResizeObserver(() => this.fitCells());
        this.resizeObserver.observe(this.$refs.root);
        this.fitCells();
    },
    beforeDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
    },
    methods: {
        fitCells() {
            const root = this.$refs.root;
            if (!root || !root.clientWidth) return;
            // 28px day-of-week column + 6px body gap + 2px safety
            const avail = root.clientWidth - 36;
            for (let cell = 20; cell >= 8; cell--) {
                const gap = cell >= 14 ? 3 : 2;
                if (this.totalWeeks * cell + (this.totalWeeks - 1) * gap <= avail || cell === 8) {
                    this.cellPx = cell;
                    return;
                }
            }
        },
        fmtHours(mins) {
            const h = Math.floor(mins / 60), m = mins % 60;
            return m ? `${h}h ${m}m` : `${h}h`;
        },
        cellClass(cell) {
            if (!cell.inRange) return '';
            const d = cell.data;
            const classes = ['l' + (d ? d.level : 0)];
            if (d && d.overlap) classes.push('overlap');
            if (d && d.contracted_mins > 0) classes.push('ddip');
            if (this.selectedDate === cell.date) classes.push('selected');
            return classes;
        },
        ariaFor(date, d) {
            let label = moment(date).format('ddd, MMM D') + ': ' + (d ? this.fmtHours(d.mins) : 'no time') + ' logged';
            if (d && d.overlap) label += ', overlapping sessions';
            if (d && d.contracted_mins > 0) label += ', time during contracted hours';
            return label;
        },
        tipHtml(cell) {
            const d = cell.data;
            let html = `<strong>${moment(cell.date).format('ddd, MMM D')}</strong><br>`;
            if (!d) return html + 'No time logged';
            const courses = d.courses.filter(g => g.kind !== 'conference').length;
            const confs = d.courses.length - courses;
            const parts = [];
            if (courses) parts.push(courses + ' course' + (courses > 1 ? 's' : ''));
            if (confs) parts.push(confs + ' conference' + (confs > 1 ? 's' : ''));
            html += `${this.fmtHours(d.mins)} across ${parts.join(' + ')}`;
            if (d.flag === 'critical') html += '<br><span class="t-flag">&#9888; Implausible daily total</span>';
            else if (d.flag === 'serious') html += '<br><span class="t-flag">&#9888; Highly unusual daily total</span>';
            else if (d.flag === 'warning') html += '<br><span class="t-flag">Unusually high daily total</span>';
            if (d.overlap) html += '<br><span class="t-flag">&#9888; Sessions overlap in time</span>';
            if (d.contracted_mins > 0) html += `<br><span class="t-flag">&#9888; ${this.fmtHours(d.contracted_mins)} during contracted hours</span>`;
            return html;
        },
        showTip(cell, event) {
            if (!cell.inRange) return;
            const root = this.$refs.root.getBoundingClientRect();
            const target = event.target.getBoundingClientRect();
            this.tip.html = this.tipHtml(cell);
            this.tip.visible = true;
            this.$nextTick(() => {
                const tipEl = this.$el.querySelector('.hm-tooltip');
                const w = tipEl ? tipEl.offsetWidth : 160;
                const h = tipEl ? tipEl.offsetHeight : 40;
                this.tip.x = Math.max(0, Math.min(target.left - root.left + this.cellPx / 2 - w / 2, root.width - w));
                this.tip.y = target.top - root.top - h - 6;
            });
        },
        select(cell) {
            if (cell.inRange) this.$emit('select', {date: cell.date, data: cell.data});
        },
    },
};
</script>

<style scoped>
.sam-heatmap {
    /* host-overridable palette */
    --sam-cell-0: #edece8;
    --sam-cell-1: #cde2fb;
    --sam-cell-2: #9ec5f4;
    --sam-cell-3: #5598e7;
    --sam-cell-4: #256abf;
    --sam-warn: #fab219;
    --sam-serious: #ec835a;
    --sam-critical: #d03b3b;
    --sam-marker: #ffffff;
    --sam-muted: #898781;
    --sam-strip: #e1e0d9;
    position: relative;
}
.hm-scroll {
    /* the fit solver should always make it fit; this is the never-crop fallback */
    overflow-x: auto;
    padding-bottom: 2px;
}
.hm-months {
    display: grid;
    font-size: 11px;
    color: var(--sam-muted);
    margin-left: 34px;
    margin-bottom: 4px;
}
.hm-body { display: flex; gap: 6px; }
.hm-dow {
    display: grid;
    gap: 3px;
    font-size: 10px;
    color: var(--sam-muted);
    width: 28px;
    text-align: right;
    flex: none;
}
.hm-dow span { display: flex; align-items: center; justify-content: flex-end; }
.hm-cells { display: grid; gap: 3px; }
.cell {
    border-radius: 3px;
    background: var(--sam-cell-0);
    position: relative;
    cursor: pointer;
}
.cell:focus-visible { outline: 2px solid var(--sam-cell-4); outline-offset: 1px; }
.cell:hover { box-shadow: inset 0 0 0 1.5px rgba(0, 0, 0, 0.3); }
.cell.l1 { background: var(--sam-cell-1); }
.cell.l2 { background: var(--sam-cell-2); }
.cell.l3 { background: var(--sam-cell-3); }
.cell.l4 { background: var(--sam-cell-4); }
.cell.l5 { background: var(--sam-warn); }
.cell.l6 { background: var(--sam-serious); }
.cell.l7 { background: var(--sam-critical); }
.cell.overlap::after {
    content: "";
    position: absolute;
    left: 31.25%; top: 31.25%;
    width: 37.5%; height: 37.5%;
    border-radius: 50%;
    background: var(--sam-marker);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35);
}
.cell.ddip::before {
    content: "";
    position: absolute;
    inset: 2px;
    border: 2px solid var(--sam-marker);
    border-radius: 2px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2), inset 0 0 0 1px rgba(0, 0, 0, 0.2);
}
.cell.ddip.overlap::after { left: 37.5%; top: 37.5%; width: 25%; height: 25%; box-shadow: none; }
.cell.selected { box-shadow: 0 0 0 2px #fff, 0 0 0 3.5px #363636; }
/* clearly separated from the daily grid so it doesn't read as a cut-off row */
.hm-weeks { display: grid; gap: 3px; margin: 14px 0 0 34px; }
.wk { height: 4px; border-radius: 2px; background: transparent; }
.wk.w-base { background: var(--sam-strip); opacity: 0.55; }
.wk.w-l1 { background: var(--sam-cell-1); }
.wk.w-l2 { background: var(--sam-cell-2); }
.wk.w-l3 { background: var(--sam-cell-3); }
.wk.w-l4 { background: var(--sam-cell-4); }
.wk.f-warning { background: var(--sam-warn); }
.wk.f-serious { background: var(--sam-serious); }
.wk.f-critical { background: var(--sam-critical); }
.hm-weeks-label { font-size: 10px; color: var(--sam-muted); margin: 2px 0 0 34px; letter-spacing: 0.04em; text-transform: uppercase; }
.hm-tooltip {
    position: absolute;
    z-index: 20;
    pointer-events: none;
    background: #363636;
    color: #fff;
    font-size: 12px;
    line-height: 1.45;
    border-radius: 6px;
    padding: 7px 10px;
    max-width: 250px;
}
/* v-html content doesn't carry the scoped attribute, and Bulma's global
   `strong` color matches the tooltip background — force inherit via deep */
.hm-tooltip >>> strong { color: inherit; }
.hm-tooltip >>> .t-flag { font-weight: 600; }
</style>
